import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/app/shell/page-header";
import { WorkspaceMark } from "@/components/app/workspace/workspace-mark";
import { useSharedHandset } from "@/components/app/phone/handset-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDockHidden } from "@/lib/workspace/dock-hidden";
import { useFacility } from "@/lib/facility/facility";
import { ExternalLink, X } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCanAnywhere, useFeature } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { CellAlertsCard } from "@/components/app/cell-alerts-card";
import { DirectoryPane } from "./-components/directory-pane";
import { ExtensionHud } from "./-components/extension-hud";
import { KeypadCard } from "./-components/keypad-card";
import { MyNumberPane } from "./-components/my-number-pane";
import { RecentPane } from "./-components/recent-pane";

export const Route = createFileRoute("/_authenticated/ws/sarah/phone")({
  beforeLoad: ({ context }) => guardRoute(context.me, "sarah-calls"),
  component: SarahPhone,
});

/** Keypad left, the people you would ring right: almost every outbound call is to someone in those
 * lists. The live call stays in the floating phone this hands the number to: one handset per
 * tab. */
function SarahPhone() {
  const { facilityId } = useFacility();
  const { state, open, setOpen, offNote } = useSharedHandset();
  const enabled = useFeature("sarah-calls");
  const canDial = useCanAnywhere("frontdesk:call");
  const [picked, setPicked] = useState<string>();
  const myLine = useQuery({
    ...orpc.frontDesk.phone.myLine.queryOptions({ input: { limit: 50 } }),
    enabled: enabled && canDial,
    staleTime: 15_000,
  });
  /* Somebody with a company number of their own opens on it, as the phone window does. */
  const hasNumber = (myLine.data?.lines.length ?? 0) > 0;
  /* A phone shows one thing at a time, so there the keypad is a tab beside the lists. */
  const mobile = useIsMobile();
  /* Until a building is picked the pad is the question of which one, asked above everything. */
  const padTab = mobile && facilityId !== "all";
  const fallback = hasNumber ? "mine" : padTab ? "keypad" : "people";
  const tab = picked && (padTab || picked !== "keypad") ? picked : fallback;
  /* A conversation on My number that holds a phone's screen puts the dock away: the heading,
     the keypad and the tabs step aside with it. */
  const reading = useDockHidden() && tab === "mine";

  const line = offNote
    ? offNote
    : state.kind === "ready" && state.extension
      ? `Your extension is ${state.extension}`
      : state.kind === "on-a-call"
        ? "You are on a call. The controls are in the phone window."
        : "Press a name, or dial a number.";

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 max-md:space-y-3">
      {reading ? null : (
        <PageHeader
          mark={<WorkspaceMark id="sarah" size={44} />}
          kicker="Sarah, Phone"
          title={canDial ? "Make a call" : "The phone"}
          meta={enabled ? line : "This community does not have the phone."}
          actions={
            enabled && canDial && !mobile ? (
              /* The same telephone, in a window that stays up while somebody
                 reads a chart. The page and the window are one handset: what is
                 typed here is dialled there. */
              <Button variant="outline" onClick={() => setOpen(!open)}>
                {open ? <X /> : <ExternalLink />}
                {open ? "Close the window" : "Open in a window"}
              </Button>
            ) : undefined
          }
        />
      )}

      {enabled ? (
        /* Stretched on a phone, where an open conversation turns this into a column
         * (`data-screen`). */
        <div className="grid-cols-[minmax(0,1fr)] grid items-start gap-6 max-md:items-stretch max-md:gap-3 lg:grid-cols-[22rem_minmax(0,1fr)]">
          {padTab || reading ? null : (
            <div className="space-y-4">
              <KeypadCard facilityId={facilityId} />
              <CellAlertsCard />
            </div>
          )}

          <Tabs
            value={tab}
            onValueChange={setPicked}
            className={facilityId === "all" ? "lg:col-span-2" : undefined}
          >
            <TabsList variant="line" className="w-full justify-start border-b" hidden={reading}>
              {padTab ? (
                <TabsTrigger value="keypad" className="flex-none px-3 max-sm:px-2">
                  Keypad
                </TabsTrigger>
              ) : null}
              {hasNumber ? (
                <TabsTrigger value="mine" className="flex-none px-3 max-sm:px-2">
                  My number
                </TabsTrigger>
              ) : null}
              <TabsTrigger value="people" className="flex-none px-3 max-sm:px-2">
                People
              </TabsTrigger>
              <TabsTrigger value="book" className="flex-none px-3 max-sm:px-2">
                Directory
              </TabsTrigger>
              <TabsTrigger value="recent" className="flex-none px-3 max-sm:px-2">
                Recent
              </TabsTrigger>
            </TabsList>
            {padTab ? (
              <TabsContent value="keypad" className="mt-4 space-y-4">
                <KeypadCard facilityId={facilityId} />
                <CellAlertsCard />
              </TabsContent>
            ) : null}
            {hasNumber ? (
              <TabsContent value="mine" className={reading ? undefined : "mt-4"}>
                <MyNumberPane facilityId={facilityId} />
              </TabsContent>
            ) : null}
            <TabsContent value="people" className="mt-4">
              <ExtensionHud facilityId={facilityId} />
            </TabsContent>
            <TabsContent value="book" className="mt-4">
              <DirectoryPane facilityId={facilityId} />
            </TabsContent>
            <TabsContent value="recent" className="mt-4">
              <RecentPane facilityId={facilityId} />
            </TabsContent>
          </Tabs>
        </div>
      ) : null}
    </div>
  );
}
